import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { Loader2, AlertTriangle } from "lucide-react";

/**
 * Phase 11 — Unified Video Editor entry point.
 *
 * Behaviour: fetch `/api/videos/{slug}/edit-context` (auto-creates a project
 * if none exists yet), then redirect to `/admin/editor/{projectId}?slug={slug}`.
 * The existing EditorEdit page then knows it was opened from the library and
 * renders the Video-Metadata section (title/slug/visibility/text overlays/POIs)
 * on top of the project-level editor.
 */
export default function VideoEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/videos/${slug}/edit-context`);
        if (cancelled) return;
        const pid = data?.project?.id;
        if (!pid) {
          setError("Konnte kein Bearbeitungs-Projekt anlegen.");
          return;
        }
        navigate(`/admin/editor/${pid}?slug=${encodeURIComponent(slug)}`, { replace: true });
      } catch (err) {
        if (!cancelled) setError(formatApiErrorDetail(err) || "Video nicht gefunden");
      }
    })();
    return () => { cancelled = true; };
  }, [slug, navigate]);

  return (
    <div className="min-h-screen bg-[#0a0a0d] text-white">
      <Topbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-16 pb-24" data-testid="video-editor-loading">
        {error ? (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-6 text-center space-y-3">
            <AlertTriangle size={32} className="mx-auto text-red-300" />
            <div className="text-lg font-semibold">Video nicht gefunden</div>
            <div className="text-sm text-red-200/80">{error}</div>
            <button
              onClick={() => navigate("/admin")}
              className="inline-flex items-center h-10 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-sm"
              data-testid="video-editor-back"
            >
              Zurück zur Bibliothek
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-3 text-[#9a9aa3] py-24">
            <Loader2 size={20} className="animate-spin" />
            <span className="text-sm">Bearbeitung wird geöffnet…</span>
          </div>
        )}
      </main>
    </div>
  );
}
